import type { IconName } from './icons';

/**
 * Цвет значка по смыслу — «живость» интерфейса, а не украшение.
 *
 * **Правило одно: цвет закреплён за сущностью, а не за экраном.** Дело всюду синее,
 * клиент сиреневый, срок и событие зелёные, документ охряной. Значок отвечает
 * на вопрос «о чём эта строка» ещё до того, как прочитан текст, — а список дня,
 * где заседание, встреча и задача набраны одним серым, читается только целиком.
 *
 * **Служебное остаётся серым.** «Назад», «Ещё», карандаш, галочка, стрелка вправо —
 * это механика экрана, а не запись юриста; красить их значило бы получить карнавал,
 * в котором цвет перестаёт что-либо значить. Красное остаётся за удалением.
 *
 * **Новых цветов не заводится.** Взяты те, что уже есть в общей с Android палитре
 * ([tokens.css](tokens.css) собирается из `Color.kt`): синий дела, сиреневый третьей
 * роли, зелёный завершённого, охра избранного. Android-версия при этом не меняется —
 * решение владельца 09.09.2026; расхождение записано в [COMPLIANCE.md](docs/COMPLIANCE.md).
 */
export type IconTone = 'case' | 'client' | 'event' | 'document' | 'neutral';

const TONES: Partial<Record<IconName, IconTone>> = {
  // Дело и всё, что его обозначает.
  folder: 'case',
  folder_fill: 'case',
  gavel: 'case',
  account_balance: 'case',
  label: 'case',
  link: 'case',

  // Клиент — человек на другом конце дела.
  person: 'client',
  group: 'client',
  group_fill: 'client',
  call: 'client',
  mail: 'client',
  forum: 'client',

  // Срок, событие, время — то, что горит.
  event: 'event',
  calendar_month: 'event',
  calendar_month_fill: 'event',
  checklist: 'event',
  checklist_fill: 'event',
  schedule: 'event',
  notifications: 'event',

  // Материалы дела: снятое, принесённое, собранное.
  picture_as_pdf: 'document',
  description: 'document',
  document_scanner: 'document',
  camera_alt: 'document',
  photo_library: 'document',
  image: 'document',
  upload_file: 'document',
  star: 'document',
  star_fill: 'document',
  keep: 'document',
  keep_fill: 'document',
};

/** Тон значка: всё, чего нет в таблице, — механика экрана и остаётся серым. */
export const toneOf = (name: IconName): IconTone => TONES[name] ?? 'neutral';
